import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import {
  fetchDeviceHistory,
  fetchIpHistory,
  fetchUsageTime,
  listActiveSessions,
  listSessionHistory,
} from "@/api/endpoints/sessions";
import type { SessionSummary } from "@/api/endpoints/sessions";
import { setUserEnabled } from "@/api/endpoints/users";
import { useAuthStore } from "@/store/authStore";

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function SessionsTable({
  sessions,
  showRevoke,
  onRevoke,
}: {
  sessions: SessionSummary[];
  showRevoke: boolean;
  onRevoke?: (s: SessionSummary) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-surface">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-text-muted">
            <th className="px-4 py-3">Username</th>
            <th className="px-4 py-3">NAS</th>
            <th className="px-4 py-3">MAC</th>
            <th className="px-4 py-3">Framed IP</th>
            <th className="px-4 py-3">Started</th>
            <th className="px-4 py-3">Duration</th>
            {showRevoke && <th className="px-4 py-3">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => (
            <tr key={s.acctuniqueid} className="border-b border-border last:border-0">
              <td className="px-4 py-3 font-mono text-text">{s.username ?? "—"}</td>
              <td className="px-4 py-3 font-mono text-text-muted">{s.nasipaddress}</td>
              <td className="px-4 py-3 font-mono text-text-muted">{s.callingstationid ?? "—"}</td>
              <td className="px-4 py-3 font-mono text-text-muted">{s.framedipaddress ?? "—"}</td>
              <td className="px-4 py-3 text-text-muted">{formatDate(s.acctstarttime)}</td>
              <td className="px-4 py-3 tabular-nums text-text">{formatDuration(s.acctsessiontime)}</td>
              {showRevoke && (
                <td className="px-4 py-3">
                  {onRevoke && (
                    <button onClick={() => onRevoke(s)} className="text-critical hover:underline">
                      Revoke access
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
          {sessions.length === 0 && (
            <tr>
              <td colSpan={showRevoke ? 7 : 6} className="px-4 py-8 text-center text-text-muted">
                No sessions.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function UserActivityPanel({ username }: { username: string }) {
  const usageQuery = useQuery({ queryKey: ["usage", username], queryFn: () => fetchUsageTime(username) });
  const deviceQuery = useQuery({ queryKey: ["devices", username], queryFn: () => fetchDeviceHistory(username) });
  const ipQuery = useQuery({ queryKey: ["ips", username], queryFn: () => fetchIpHistory(username) });

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <div className="rounded-lg border border-border bg-surface p-4">
        <h3 className="mb-2 text-xs uppercase tracking-wide text-text-muted">Total usage</h3>
        <p className="text-2xl font-semibold tabular-nums text-text">
          {usageQuery.data ? formatDuration(usageQuery.data.total_seconds) : "…"}
        </p>
        <p className="text-xs text-text-muted">{usageQuery.data?.session_count ?? 0} sessions</p>
      </div>
      <div className="rounded-lg border border-border bg-surface p-4">
        <h3 className="mb-2 text-xs uppercase tracking-wide text-text-muted">Device history (MAC)</h3>
        <ul className="space-y-1 font-mono text-xs text-text">
          {deviceQuery.data?.map((d) => (
            <li key={d.callingstationid}>
              {d.callingstationid} <span className="text-text-muted">×{d.session_count}</span>
            </li>
          ))}
          {deviceQuery.data?.length === 0 && <li className="text-text-muted">No data</li>}
        </ul>
      </div>
      <div className="rounded-lg border border-border bg-surface p-4">
        <h3 className="mb-2 text-xs uppercase tracking-wide text-text-muted">IP history</h3>
        <ul className="space-y-1 font-mono text-xs text-text">
          {ipQuery.data?.map((ip) => (
            <li key={ip.framedipaddress}>
              {ip.framedipaddress} <span className="text-text-muted">×{ip.session_count}</span>
            </li>
          ))}
          {ipQuery.data?.length === 0 && <li className="text-text-muted">No data</li>}
        </ul>
      </div>
    </div>
  );
}

export function SessionsPage() {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Revoking access disables the SQL user account (Users → SQL tab), the
  // same action available there — gated on the same permission it actually
  // calls, not a separate "disconnect" permission the backend doesn't check
  // for this anymore.
  const canRevoke = hasPermission("users.manage");
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<"active" | "history">("active");
  const [historyUsername, setHistoryUsername] = useState("");
  const [lookupUsername, setLookupUsername] = useState("");

  const activeQuery = useQuery({
    queryKey: ["sessions", "active"],
    queryFn: () => listActiveSessions(1),
    refetchInterval: 15_000,
    enabled: tab === "active",
  });

  const historyQuery = useQuery({
    queryKey: ["sessions", "history", historyUsername],
    queryFn: () => listSessionHistory(historyUsername, 1),
    enabled: tab === "history",
  });

  // Real-time RADIUS CoA disconnect doesn't work against this network (the
  // APs never ACK it — confirmed at the packet level, not a bug here) and
  // fixing that would mean integrating with the UniFi Controller's own API
  // instead. Revoking access is the practical alternative: it can't force
  // an already-connected device off Wi-Fi instantly, but it does stop the
  // account from reconnecting once its current session ends or it next
  // needs to re-authenticate.
  const revokeMutation = useMutation({
    mutationFn: (s: SessionSummary) => setUserEnabled(s.username ?? "", false),
    onSuccess: (_data, s) => {
      toast.success(
        `${s.username}'s account is disabled — it can no longer log back in. ` +
          "The device shown here may stay connected until it next needs to re-authenticate.",
        { duration: 10_000 },
      );
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err: Error, s) => {
      if (err.message.toLowerCase().includes("not found")) {
        toast.error(
          `${s.username} isn't a SQL-managed account (likely file-based) — can't be disabled from here yet.`,
          { duration: 8000 },
        );
      } else {
        toast.error(err.message);
      }
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        <button
          onClick={() => setTab("active")}
          className={`rounded-md px-3 py-1.5 text-sm ${tab === "active" ? "bg-surface-2 text-text" : "text-text-muted"}`}
        >
          Active ({activeQuery.data?.total ?? "…"})
        </button>
        <button
          onClick={() => setTab("history")}
          className={`rounded-md px-3 py-1.5 text-sm ${tab === "history" ? "bg-surface-2 text-text" : "text-text-muted"}`}
        >
          History
        </button>
      </div>

      {tab === "active" && (
        <SessionsTable
          sessions={activeQuery.data?.items ?? []}
          showRevoke={canRevoke}
          onRevoke={(s) => {
            if (
              window.confirm(
                `Disable ${s.username ?? "this user"}'s account?\n\n` +
                  "This won't force the device off Wi-Fi right now — it stops the account " +
                  "from reconnecting once this session ends or it next needs to re-authenticate.",
              )
            ) {
              revokeMutation.mutate(s);
            }
          }}
        />
      )}

      {tab === "history" && (
        <>
          <input
            placeholder="Filter by username…"
            value={historyUsername}
            onChange={(e) => setHistoryUsername(e.target.value)}
            className="w-64 rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-text outline-none focus:border-accent"
          />
          <SessionsTable sessions={historyQuery.data?.items ?? []} showRevoke={false} />
        </>
      )}

      <div className="mt-4 border-t border-border pt-4">
        <h2 className="mb-2 font-mono text-xs uppercase tracking-wide text-text-muted">User activity lookup</h2>
        <input
          placeholder="Username…"
          value={lookupUsername}
          onChange={(e) => setLookupUsername(e.target.value)}
          className="mb-4 w-64 rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-text outline-none focus:border-accent"
        />
        {lookupUsername && <UserActivityPanel username={lookupUsername} />}
      </div>
    </div>
  );
}
